import { ThrowLiteError } from '../lite-error.js';
import { BU } from '../engine/gpu-flags.js';
import { align, createMappedBuffer } from './gpu-buffers.js';

function createStorageBuffer(engine, data, label) {
  const byteLength = align(Math.max(data.byteLength, 4), 4);
  const bytes = new Uint8Array(byteLength);
  bytes.set(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
  const storage = { byteLength };
  Object.defineProperties(storage, {
    _buffer: { value: createMappedBuffer(engine, bytes, BU.STORAGE, label), writable: true },
    _destroyed: { value: false, writable: true },
    _data: { value: bytes, writable: true },
    _engine: { value: engine },
    _label: { value: label }
  });
  (engine._storageBuffers ??= /* @__PURE__ */ new Set()).add(storage);
  if (!engine._storageRequiredLimits) {
    const limits = engine._device.limits;
    if (limits) {
      engine._storageRequiredLimits = {
        maxBufferSize: limits.maxBufferSize,
        maxStorageBufferBindingSize: limits.maxStorageBufferBindingSize,
        maxStorageBuffersPerShaderStage: limits.maxStorageBuffersPerShaderStage
      };
    }
  }
  engine._rebuildStorageBuffers ??= () => _rebuildStorageBuffers(engine);
  engine._disposeStorageBuffers ??= () => _disposeStorageBuffers(engine);
  return storage;
}
function _getStorageBufferHandle(engine, buffer) {
  if (buffer._destroyed || !buffer._data) {
    ThrowLiteError(448);
  }
  if (buffer._engine !== engine) {
    ThrowLiteError(449);
  }
  if (!engine._storageBuffers?.has(buffer)) {
    ThrowLiteError(450);
  }
  return buffer._buffer;
}
function updateStorageBuffer(engine, buffer, data, byteOffset = 0) {
  if (buffer._destroyed) {
    ThrowLiteError(451);
  }
  if (!("_engine" in buffer)) {
    ThrowLiteError(452);
  }
  if (buffer._engine !== engine) {
    ThrowLiteError(453);
  }
  if (!engine._storageBuffers?.has(buffer)) {
    ThrowLiteError(454);
  }
  if (!Number.isInteger(byteOffset) || byteOffset < 0 || (byteOffset & 3) !== 0) {
    ThrowLiteError(455);
  }
  if ((data.byteLength & 3) !== 0) {
    ThrowLiteError(456);
  }
  if (byteOffset + data.byteLength > buffer.byteLength) {
    ThrowLiteError(457, buffer.byteLength);
  }
  if (data.byteLength === 0) {
    return;
  }
  engine._device.queue.writeBuffer(buffer._buffer, byteOffset, data.buffer, data.byteOffset, data.byteLength);
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  buffer._data.set(bytes, byteOffset);
}
function disposeStorageBuffer(buffer) {
  if (buffer._destroyed) {
    return;
  }
  if (!("_engine" in buffer) || !buffer._engine._storageBuffers?.has(buffer)) {
    ThrowLiteError(458);
  }
  buffer._buffer?.destroy();
  buffer._buffer = null;
  buffer._engine._storageBuffers.delete(buffer);
  buffer._data = null;
  buffer._destroyed = true;
  if (buffer._engine._storageBuffers.size === 0) {
    buffer._engine._storageBuffers = void 0;
    buffer._engine._storageRequiredLimits = void 0;
    buffer._engine._rebuildStorageBuffers = void 0;
    buffer._engine._disposeStorageBuffers = void 0;
  }
}
function _rebuildStorageBuffers(engine) {
  for (const buffer of engine._storageBuffers ?? []) {
    if (!buffer._destroyed && buffer._data) {
      buffer._buffer = createMappedBuffer(engine, buffer._data, BU.STORAGE, buffer._label);
    }
  }
}
function _disposeStorageBuffers(engine) {
  for (const buffer of [...engine._storageBuffers ?? []]) {
    disposeStorageBuffer(buffer);
  }
}

export { _disposeStorageBuffers, _getStorageBufferHandle, _rebuildStorageBuffers, createStorageBuffer, disposeStorageBuffer, updateStorageBuffer };
//# sourceMappingURL=storage-buffer.js.map
